/**
 * The Config object holds all parameters needed to run the web app in a given environment.
 * The parameters themselves are defined in getConfig, and pruned at build time based on NODE_ENV.
 * Only the configuration data for the selected environment will persist in the build artifact.
 */
export type Config = {
  DECODABLE_ENV: "local" | "dev" | "prod";
  DECODABLE_AUTH0_DOMAIN: string;
  DECODABLE_AUTH0_SPA_CLIENT_ID: string;
  DECODABLE_AUTH0_API_AUDIENCE: string;
  DECODABLE_CELL_API_URL_TEMPLATE: string;
  DECODABLE_ACCOUNT_API_URL_TEMPLATE: string;
  DATADOG_CLIENT_TOKEN: string;
  STRIPE_PUBLISHABLE_KEY?: string;
  INTERCOM_APP_ID: string;
  GIT_SHA: string;
};

const GIT_SHA = process.env.GITHUB_SHA || "";

/**
 * Returns the configuration data for the application (see {@link Config}).
 * Called immediately on module instantiation (early in the process lifecycle),
 * and fails fast if the app config cannot be fully determined.
 */
function getConfig(): Config {
  // Parcel will inline the NODE_ENV & optimize these checks away in the final build.
  // https://parceljs.org/features/production/#development-branch-removal

  if (process.env.NODE_ENV == "test" || process.env.NODE_ENV == "local") {
    return {
      DECODABLE_ENV: "local",
      DECODABLE_AUTH0_DOMAIN: "auth.dev.decodable.co",
      DECODABLE_AUTH0_SPA_CLIENT_ID: "MgzofdOeT44CI21rayvJc851189AjXme",
      DECODABLE_AUTH0_API_AUDIENCE: "https://api.dev.decodable.co/",
      DECODABLE_CELL_API_URL_TEMPLATE: "http://localhost:8080",
      DECODABLE_ACCOUNT_API_URL_TEMPLATE: "http://localhost:8080",
      DATADOG_CLIENT_TOKEN: "",
      STRIPE_PUBLISHABLE_KEY:
        "pk_test_51MBi3pGOKEgqvgttZLqhafBAYLPMFNcvaDA3RwBisSX64ACiSFkMbjCJyGEwoZ8NrT7TCDgNHKUwC5waG0J23ncX00HgVtOXeB",
      INTERCOM_APP_ID: "tzz6asfz",
      GIT_SHA,
    };
  }

  if (process.env.NODE_ENV == "development") {
    return {
      DECODABLE_ENV: "dev",
      DECODABLE_AUTH0_DOMAIN: "auth.dev.decodable.co",
      DECODABLE_AUTH0_SPA_CLIENT_ID: "MgzofdOeT44CI21rayvJc851189AjXme",
      DECODABLE_AUTH0_API_AUDIENCE: "https://api.dev.decodable.co/",
      DECODABLE_CELL_API_URL_TEMPLATE: "https://{}.cell.dev.decodable.co",
      DECODABLE_ACCOUNT_API_URL_TEMPLATE: "https://{}.api.dev.decodable.co",
      DATADOG_CLIENT_TOKEN: "pubbfb96f684eadff12b5a57f14a17f1cf9",
      STRIPE_PUBLISHABLE_KEY:
        "pk_test_51MBi3pGOKEgqvgttZLqhafBAYLPMFNcvaDA3RwBisSX64ACiSFkMbjCJyGEwoZ8NrT7TCDgNHKUwC5waG0J23ncX00HgVtOXeB",
      INTERCOM_APP_ID: "tzz6asfz",
      GIT_SHA,
    };
  }

  // This is typically the preferred way to work on the UI, as the
  // prod APIs are more stable than what's available on the various
  // dev cells. It's more forgiving than the
  if (process.env.NODE_ENV == "local-production") {
    if (!process.env.STRIPE_PUBLISHABLE_KEY) {
      console.warn(
        `process.env.STRIPE_PUBLISHABLE_KEY not set. Some billing features will not function.`
      );
    }
    return {
      DECODABLE_ENV: "prod",
      DECODABLE_AUTH0_DOMAIN: "auth.decodable.co",
      DECODABLE_AUTH0_SPA_CLIENT_ID: "vTftnWzHDMDePlGNPS7AD1XrTqrkMLjC",
      DECODABLE_AUTH0_API_AUDIENCE: "https://api.decodable.co/",
      DECODABLE_CELL_API_URL_TEMPLATE: "https://{}.cell.decodable.co",
      DECODABLE_ACCOUNT_API_URL_TEMPLATE: "https://{}.api.decodable.co",
      DATADOG_CLIENT_TOKEN: "",
      STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
      INTERCOM_APP_ID: "wm7dv1g4",
      GIT_SHA,
    };
  }

  if (process.env.NODE_ENV == "production") {
    if (!process.env.STRIPE_PUBLISHABLE_KEY) {
      throw new Error(
        `Could not configure app. STRIPE_PUBLISHABLE_KEY not set.`
      );
    }

    return {
      DECODABLE_ENV: "prod",
      DECODABLE_AUTH0_DOMAIN: "auth.decodable.co",
      DECODABLE_AUTH0_SPA_CLIENT_ID: "vTftnWzHDMDePlGNPS7AD1XrTqrkMLjC",
      DECODABLE_AUTH0_API_AUDIENCE: "https://api.decodable.co/",
      DECODABLE_CELL_API_URL_TEMPLATE: "https://{}.cell.decodable.co",
      DECODABLE_ACCOUNT_API_URL_TEMPLATE: "https://{}.api.decodable.co",
      DATADOG_CLIENT_TOKEN: "pubddc00d66c6da7c59323f74b8b45578fa",
      STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
      INTERCOM_APP_ID: "wm7dv1g4",
      GIT_SHA,
    };
  }

  throw new Error(
    `Could not configure app. Unrecognized NODE_ENV ${process.env.NODE_ENV}`
  );
}

export const config = getConfig();
export default config;
